import { z } from 'zod';
import { financeReconciliationSha256Schema, financeReconciliationStatementImportCommandSchema, } from './finance-reconciliation.contract.js';
import { financeInvoiceCommandIdSchema } from './finance-invoice.contract.js';
export const financeReconciliationAdaptersCapability = 'finance.reconciliation-adapters.v1';
// Adapter input is transported as JSON. Keep the source below the API's 1 MB
// request ceiling, including the command envelope and JSON encoding overhead.
export const financeReconciliationAdapterMaximumSourceBytes = 750_000;
export const financeReconciliationAdapterKeySchema = z.enum([
    'chase-ink-text-v1',
    'stripe-report-csv-v1',
]);
export const financeReconciliationAdapterInputSchema = z.strictObject({
    adapterKey: financeReconciliationAdapterKeySchema,
    commandId: financeInvoiceCommandIdSchema,
    statementId: z.string().uuid(),
    sourceText: z.string().min(1).max(financeReconciliationAdapterMaximumSourceBytes),
    sourceSha256: financeReconciliationSha256Schema,
    sourceFileName: z.string().trim().min(1).max(240).nullable().default(null),
    sourceLabel: z.string().trim().min(1).max(120),
    sourceReference: z.string().trim().min(1).max(240).nullable().default(null),
    accountName: z.string().trim().min(1).max(200),
    accountLastFour: z.string().regex(/^\d{4}$/).nullable().default(null),
    reason: z.string().trim().min(1).max(2_000),
});
export const financeReconciliationAdapterEvidenceCodes = [
    'invalid-date',
    'invalid-amount',
    'missing-date',
    'missing-amount',
    'missing-description',
    'unparsed-transaction-row',
    'duplicate-header',
    'period-derived-from-rows',
    'source-row-limit-reached',
];
export const financeReconciliationAdapterEvidenceCodeSchema = z.enum(financeReconciliationAdapterEvidenceCodes);
export const financeReconciliationAdapterEvidenceSchema = z.strictObject({
    code: financeReconciliationAdapterEvidenceCodeSchema,
    sourceRowNumber: z.number().int().positive().nullable(),
    message: z.string().trim().min(1).max(240),
});
export const financeReconciliationAdapterResultSchema = z.strictObject({
    contractVersion: z.literal(financeReconciliationAdaptersCapability),
    adapterKey: financeReconciliationAdapterKeySchema,
    adapterVersion: z.literal(1),
    sourceSha256: financeReconciliationSha256Schema,
    acceptedRowCount: z.number().int().nonnegative(),
    rejectedRowCount: z.number().int().nonnegative(),
    rejectsTruncated: z.boolean(),
    warnings: z.array(financeReconciliationAdapterEvidenceSchema).max(100),
    rejects: z.array(financeReconciliationAdapterEvidenceSchema).max(500),
    importCommand: financeReconciliationStatementImportCommandSchema.nullable(),
}).superRefine((value, context) => {
    if ((value.acceptedRowCount > 0) !== (value.importCommand !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['importCommand'],
            message: 'Normalized rows and import command must be present together.',
        });
    }
    if (value.importCommand !== null && value.importCommand.rows.length !== value.acceptedRowCount) {
        context.addIssue({
            code: 'custom',
            path: ['acceptedRowCount'],
            message: 'Accepted row count must match the import command.',
        });
    }
    if (!value.rejectsTruncated && value.rejects.length !== value.rejectedRowCount) {
        context.addIssue({
            code: 'custom',
            path: ['rejectedRowCount'],
            message: 'Complete reject evidence must match the rejected row count.',
        });
    }
});
export const financeReconciliationAdapterResponseSchema = z.strictObject({
    data: financeReconciliationAdapterResultSchema,
});
//# sourceMappingURL=finance-reconciliation-adapter.contract.js.map